import { Component, Input, OnInit, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AgremoService } from '../../../core/services/agremo.service';
import { WebSocketService } from '../../../core/services/websocket.service';
import { AgremoMap, AgremoMapStatus, AgremoMapStatusEvent, CreateMapDto } from '../../../core/models/agremo-analysis.model';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Field Maps Card Component
 *
 * Displays and manages maps for a specific field. Allows uploading new maps
 * (GeoTIFF imagery) and shows processing status. Maps must be in 'ready'
 * status before they can be used for analysis.
 *
 * Features:
 * - Table of maps with status badges and analysis counts
 * - Inline upload form (drag-drop + metadata)
 * - Real-time status updates via WebSocket
 * - Delete maps
 */
@Component({
  selector: 'app-field-maps-card',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ConfirmModalComponent, MaxLengthDirective],
  templateUrl: './field-maps-card.component.html',
  styleUrls: ['./field-maps-card.component.scss']
})
export class FieldMapsCardComponent implements OnInit, OnDestroy {
  @Input({ required: true }) fieldId!: string;
  @Input() agremoFieldId?: string;

  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private agremoService = inject(AgremoService);
  private webSocketService = inject(WebSocketService);
  private destroy$ = new Subject<void>();

  // Delete state
  private pendingDeleteMapId: string | null = null;

  // Recheck state
  recheckingMapId = signal<string | null>(null);

  maps = signal<AgremoMap[]>([]);
  isLoading = signal(false);
  readonly isConfigured = this.agremoService.isConfigured;
  showUploadForm = signal(false);

  // Upload state
  uploadedFile = signal<File | null>(null);
  isUploading = signal(false);
  uploadError = signal<string | null>(null);
  isDragOver = signal(false);

  // Upload form fields
  mapName = '';
  mapDate = '';
  cameraType = 'RGB';

  async ngOnInit(): Promise<void> {
    const configured = await this.agremoService.checkStatus();
    if (configured !== true) return;

    this.loadMaps();
    this.setupWebSocketSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadMaps(): Promise<void> {
    this.isLoading.set(true);
    try {
      const maps = await this.agremoService.loadMaps(this.fieldId);
      this.maps.set(maps);
    } finally {
      this.isLoading.set(false);
    }
  }

  private setupWebSocketSubscriptions(): void {
    this.webSocketService.on<AgremoMapStatusEvent>('agremo-map:status')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.maps.update(maps =>
          maps.map(m => m.id === event.mapId
            ? {
                ...m,
                status: event.status as AgremoMapStatus,
                // 'failed' carries the specific reason in the event itself; any other
                // status (e.g. 'ready') clears a stale reason from a prior failure. A
                // 'failed' event with no reason must NOT fall back to the previous
                // failure's message -- that would misattribute an old cause to a new
                // failure (Copilot catch on PR #148 re-review).
                errorMessage: event.status === 'failed' ? (event.error ?? null) : null,
              }
            : m
          )
        );
      });
  }

  // ============================================
  // UPLOAD FORM
  // ============================================

  toggleUploadForm(): void {
    this.showUploadForm.update(v => !v);
    if (this.showUploadForm()) {
      this.resetUploadForm();
    }
  }

  private resetUploadForm(): void {
    this.uploadedFile.set(null);
    this.uploadError.set(null);
    this.isUploading.set(false);
    this.mapName = '';
    const now = new Date();
    this.mapDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    this.cameraType = 'RGB';
  }

  // ============================================
  // DRAG & DROP
  // ============================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelect(files[0]);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelect(input.files[0]);
    }
  }

  private handleFileSelect(file: File): void {
    const allowedExtensions = ['tif', 'tiff', 'jpg', 'jpeg', 'png', 'zip'];
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (!allowedExtensions.includes(extension)) {
      this.uploadError.set('Invalid file type. Please upload a TIFF, JPEG, PNG, or ZIP file.');
      return;
    }

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError.set('File too large. Maximum size is 500MB.');
      return;
    }

    this.uploadedFile.set(file);
    this.uploadError.set(null);

    if (!this.mapName) {
      this.mapName = file.name.replace(/\.[^.]+$/, '');
    }
  }

  removeFile(): void {
    this.uploadedFile.set(null);
    this.uploadError.set(null);
  }

  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  // ============================================
  // SUBMIT UPLOAD
  // ============================================

  get canSubmit(): boolean {
    return !!this.uploadedFile() && !!this.mapName && !!this.mapDate && !this.isUploading();
  }

  async submitUpload(): Promise<void> {
    const file = this.uploadedFile();
    if (!file || !this.canSubmit) return;

    this.isUploading.set(true);
    this.uploadError.set(null);

    try {
      // Step 1: Upload file to S3
      const { s3Url } = await this.agremoService.uploadImagery(file);

      // Step 2: Create map record (queues background processing)
      const dto: CreateMapDto = {
        fieldId: this.fieldId,
        mapName: this.mapName,
        mapDate: this.mapDate,
        cameraType: this.cameraType,
        s3Url,
      };

      const newMap = await this.agremoService.createMap(dto);
      if (newMap) {
        this.maps.update(maps => [newMap, ...maps]);
      }

      // Reset form and hide
      this.showUploadForm.set(false);
      this.resetUploadForm();
    } catch (error: any) {
      console.error('Map upload failed:', error);
      this.uploadError.set(error.message || 'Upload failed. Please try again.');
    } finally {
      this.isUploading.set(false);
    }
  }

  // ============================================
  // DELETE
  // ============================================

  confirmDeleteMap(map: AgremoMap): void {
    this.pendingDeleteMapId = map.id;
    this.confirmModal.open({
      title: 'Delete Map',
      message: `Are you sure you want to delete "${map.mapName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  async onDeleteConfirmed(): Promise<void> {
    const mapId = this.pendingDeleteMapId;
    if (!mapId) return;

    this.confirmModal.setProcessing(true);
    try {
      await this.agremoService.deleteMap(mapId);
      this.maps.update(maps => maps.filter(m => m.id !== mapId));
      this.confirmModal.setProcessing(false);
      this.confirmModal.close();
    } catch (error: any) {
      console.error('Delete failed:', error);
      this.confirmModal.setProcessing(false);
    } finally {
      this.pendingDeleteMapId = null;
    }
  }

  // ============================================
  // RECHECK
  // ============================================

  async recheckMap(map: AgremoMap): Promise<void> {
    if (this.recheckingMapId() !== null) return;

    this.recheckingMapId.set(map.id);
    try {
      await this.agremoService.recheckMap(map.id);
      // Reload from API to get the authoritative status
      await this.loadMaps();
    } catch (error: any) {
      console.error('Failed to recheck map:', error);
    } finally {
      this.recheckingMapId.set(null);
    }
  }

  // ============================================
  // STATUS HELPERS
  // ============================================

  getStatusText(status: AgremoMapStatus): string {
    return this.agremoService.getMapStatusText(status);
  }

  getStatusBadgeClass(status: AgremoMapStatus): string {
    return this.agremoService.getMapStatusBadgeClass(status);
  }

  isProcessing(status: AgremoMapStatus): boolean {
    return status === 'uploading' || status === 'processing';
  }
}
